import { Widget, CookieService, DomHelpers, ViewWidgetHelper, ViewCmdType, ViewWidget, TimecodeWidget, ImSys, Loc } from '@nordbox/core';
import { PlayerEngine } from './PlayerEngine.js';
import { PlayerViewCmd } from './PlayerViewCmd.js';
import { PlayerServerCommand } from './cmd/PlayerServerCommand.js';
import { PlayerMode } from './IPlayerWidget.js';

// widgets/player/PlayerBaseWidget.ts
// Базовый виджет плеера. Без jQuery. Управляет Engine, держит общий UI-скелет.
class PlayerBaseWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Текущий режим */
        this.currentMode = PlayerMode.off;
        /** Текущие опции режима (как в легаси) */
        this.currentModeOptions = undefined;
        /** Обработчики кнопок «шаг» (держим рядом) */
        this._onPrevSec = () => this._nudge(-this._stepSeconds);
        this._onNextSec = () => this._nudge(+this._stepSeconds);
        this._onPrevFrame = () => this._nudge(-1 / this._frameRate);
        this._onNextFrame = () => this._nudge(1 / this._frameRate);
        /** Шаг перемотки по секундам (по умолчанию 1 сек) */
        this._stepSeconds = 1;
        /** FPS для шагов по кадрам (как в легаси 25) */
        this._frameRate = 25;
        /** Подвинуть позицию на delta секунд (ставим паузу, как в старом коде) */
        this._nudge = (delta) => {
            if (!this.mediaEl.paused)
                this.pause();
            const dur = this.mediaEl.duration || 0;
            let ct = this.engine.currentTime() + delta;
            ct = dur > 0 ? Math.min(Math.max(0, ct), dur) : Math.max(0, ct);
            this.seek(ct);
        };
        /** Избранные элементы UI */
        this.els = {
            btnPlay: null,
            btnMute: null,
            /** Прогресс воспроизведения (0..100) */
            playProgress: null,
            /** Прогресс загрузки буфера (0..100) */
            loadProgress: null,
            /** Слайдер позиции воспроизведения (single) */
            playSlider: null,
            /** Слайдер диапазона сцены (range) */
            sceneRange: null,
            /** Слайдер громкости (0..1, step 0.01) */
            volumeSlider: null,
            title: null,
            tkContainer: null,
            imgContainer: null,
            sceneContainer: null,
            labelContainer: null,
            filmPartLinking: null,
            filmLinking: null,
        };
        /** Коррекция таймкода, переносим как в легаси */
        this.tkCorrection = 0;
        this._onCloseVs = () => this.pause();
        /** @private keyup → делегирует в _onKey */
        this._onKeyUp = (e) => this._onKey(e, true);
        /** @private keydown → делегирует в _onKey */
        this._onKeyDown = (e) => this._onKey(e, false);
        /**
         * Делегирование кликов по общим кнопкам плеера:
         *  - bt-set-in / bt-set-out / bt-set-img
         *  - bt-go-in / bt-go-out / bt-go-img
         *  - bt-play-inout (одноразовое проигрывание отрезка без лупа)
         * Исключаем здесь: bt-save-tc (уже покрыт _wireSaveBtn) и bt-play (есть свой хендлер).
         */
        this._onCommonBtClick = (ev) => {
            const target = ev.target;
            const btn = target?.closest('.bt');
            if (!btn || !this.element.contains(btn))
                return;
            // Эти кнопки обрабатываются в других местах базы
            if (btn.classList.contains('bt-save-tc'))
                return;
            if (btn.classList.contains('bt-play'))
                return;
            // Без источника — действий нет (поведение как в наследниках)
            if (!this.mediaEl.src)
                return;
            const ct = this.engine.currentTime();
            const corr = this.tkCorrection;
            this.mediaEl.duration || 0;
            if (btn.classList.contains('bt-set-in')) {
                if (!this.mediaEl.paused)
                    this.pause();
                this.setIn(Math.max(0, ct - corr));
                if (this.currentMode === PlayerMode.preview || this.currentMode === PlayerMode.editFootage || this.currentMode === PlayerMode.newFootage)
                    this.setImg(this.getIn() + (this.getOut() - this.getIn()) * 0.2);
                return;
            }
            if (btn.classList.contains('bt-set-out')) {
                if (!this.mediaEl.paused)
                    this.pause();
                this.setOut(Math.max(0, ct - corr));
                this.setImg(this.getIn() + (this.getOut() - this.getIn()) * 0.2);
                return;
            }
            if (btn.classList.contains('bt-set-img')) {
                this.setImg(Math.max(0, ct - corr));
                return;
            }
            if (btn.classList.contains('bt-go-in')) {
                this.seek((this.getIn() || 0) + corr);
                return;
            }
            if (btn.classList.contains('bt-go-out')) {
                this.seek((this.getOut() || 0) + corr);
                return;
            }
            if (btn.classList.contains('bt-go-img')) {
                this.seek((this.getImg() || 0) + corr);
                return;
            }
            if (btn.classList.contains('bt-play-inout')) {
                const i = this.getIn();
                const o = this.getOut();
                if (Number.isFinite(i) && Number.isFinite(o) && o > i) {
                    this.playSegment(i, o); // одноразово, без лупа
                }
                return;
            }
        };
        /**
         * Универсальный toggle воспроизведения:
         * - если источника нет — грузим по fg или через <source>
         * - если есть — обычный play/pause (без лупа и сегмента)
         */
        this.playPause = () => {
            const hasSrc = !!(this.mediaEl.currentSrc ||
                this.mediaEl.src ||
                this.mediaEl.querySelector('source[src]') // Поиск ресурсов указанных как src
            );
            if (!hasSrc) {
                // Если был предустановлен фильм, запускаем его
                if (this._film) {
                    // OLD: this.playFilm(this.element.attr('data-film'));
                    this.playFilm(this._film);
                }
                // Если была предустановлена файловая группа, запускаем ее
                else if (this._fg) {
                    // OLD: this.playVideosource(this.element.attr('data-fg'));
                    this.playVideosource(this._fg);
                }
            }
            else {
                if (this.mediaEl.paused) {
                    // обычное воспроизведение — без сегмента и без лупа
                    this._segmentActive = false;
                    this.engine.setLoop({});
                    this._pauseOtherPlayers(); // ← добавили
                    this.engine.play();
                }
                else {
                    this.pause();
                }
            }
        };
        /** Флаг «сейчас во фуллскрине» */
        this._fullscreen = false;
        /** Переключить fullscreen для контейнера плеера */
        this._toggleFullscreen = () => {
            const host = this.element;
            if (!this._fullscreen) {
                (host.requestFullscreen?.bind(host)
                    || host.webkitRequestFullscreen
                    || host.msRequestFullscreen
                    || host.mozRequestFullScreen)?.();
            }
            else {
                (document.exitFullscreen
                    || document.webkitExitFullscreen
                    || document.msExitFullscreen
                    || document.mozCancelFullScreen)?.call(document);
            }
        };
        /** Реакция на смену fullscreen (обновляем флаг и кнопки) */
        this._onFullscreenChange = () => {
            // считаем флагом, что fullscreen активен на нашем контейнере (или внутри него)
            const fsEl = (document.fullscreenElement
                || document.webkitFullscreenElement
                || document.mozFullScreenElement
                || document.msFullscreenElement);
            const nextFullscreen = !!fsEl && (fsEl === this.element || this.element.contains(fsEl));
            // 2026-03-30: fullscreen-событие может прийти несколько раз из-за vendor-prefixed
            // обработчиков, поэтому downstream-логику дергаем только при реальной смене состояния.
            const fullscreenChanged = this._fullscreen !== nextFullscreen;
            this._fullscreen = nextFullscreen;
            // 2026-03-30: для базового плеера fullscreen отражаем только через active-состояние
            // кнопки, без отдельной size-модели как в NF-плеере.
            this.element.querySelector('.bt-size-full')
                ?.classList.toggle('active', this._fullscreen);
            if (fullscreenChanged) {
                this._onFullscreenStateChanged(this._fullscreen);
            }
        };
        // ======= Локальные флаги для drag/идемпотентного обновления =======
        /** Флаг «пользователь сейчас двигает playSlider» — чтобы не перетерать UI коллбэком движка */
        this._draggingPlay = false;
        /** Флаг «пользователь сейчас двигает sceneRange» */
        this._draggingRange = false;
        /** Флаг «пользователь сейчас двигает volumeSlider» */
        this._draggingVolume = false;
        /** Включить/выключить кликабельность контролов */
        this._setControlsEnabled = (on) => {
            const set = (el, enabled) => {
                if (!el)
                    return;
                el.toggleAttribute('disabled', !enabled);
                el.classList.toggle('off', !enabled);
            };
            set(this.els.playSlider, on);
            set(this.els.sceneRange, on);
            set(this.els.volumeSlider, on);
            // кнопки (кроме .bt-play)
            this.element.querySelectorAll('.bt:not(.bt-play)').forEach(b => {
                b.classList.toggle('off', !on);
            });
        };
        // вызывать в нужные моменты:
        this._refreshControlsByState = () => {
            const hasSrc = !!(this.mediaEl.currentSrc ||
                this.mediaEl.getAttribute('src') ||
                this.mediaEl.querySelector('source[src]'));
            this._setControlsEnabled(hasSrc);
        };
        this._setPlayUi = (isPlaying) => {
            // класс .pause висит на .bt-play, когда идёт воспроизведение
            this.els.btnPlay?.classList.toggle('pause', isPlaying);
            this._refreshControlsByState();
        };
        /** Показать/скрыть кружок загрузки */
        this._setLoading = (on = true) => {
            const ld = this.element.querySelector('.loading-circle');
            if (!ld)
                return;
            ld.classList.toggle('on', on);
        };
        /** Включить лоадер */
        this._onLoadingOn = () => this._setLoading(true);
        /** Выключить лоадер */
        this._onLoadingOff = () => this._setLoading(false);
        /** Однократное воспроизведение фрагмента (активируется по bt-play-inout) */
        this._segmentActive = false;
        /** Правая граница фрагмента, сек. */
        this._segmentEnd = 0;
        /** локальный holder на remover (рядом с методом) */
        this._offPlaySlider = null;
        // ─── рядом с _wireSceneRange (приватная «служебная» переменная для сравнения пар) ───
        /** Последняя зафиксированная пара значений диапазона (для определения активной ручки) */
        this._lastRangePair = null;
        this._offSceneRange = null;
        this._offVolume = null;
        // --- Обработчики кликов (как стрелочные, чтобы легко снимать) ---
        this._onMuteClick = () => this.mute(!(this.mediaEl.muted));
    }
    // --- Инициализация ---
    _create(options) {
        // Найдём <video|audio>
        const media = this.element.querySelector('video, audio');
        if (!media)
            throw new Error('PlayerWidgetBase: <video> или <audio> не найден');
        this.mediaEl = media;
        this._fg = options.fg;
        this._film = options.film;
        this.controller = options.cfg ?? 'view="nf.vsmodule.playerController"';
        this.abortController = new AbortController();
        this.validateInOut = options.validateinout ?? false;
        // Инициализация Engine
        this.engine = new PlayerEngine(this.mediaEl, {
            timeupdate: (t) => this._onEngineTime(t),
            durationchange: (d) => this._onEngineDuration(d),
            progress: (b) => this._onEngineProgress(b),
            playing: () => {
                this._setPlayUi(true);
                this.element.classList.remove('nf-player-hidden');
            },
            paused: () => {
                this._setPlayUi(false);
                // Совместимость VS: отдать текущее время наружу при паузе
                this.element.dispatchEvent(new CustomEvent('player-time-changed', {
                    detail: {
                        timecode: this.engine.currentTime()
                    }
                }));
            },
            ended: () => {
                this._setPlayUi(false);
                if (options.playloop === 'on') {
                    this.seek(0);
                    this.play();
                }
            }
        });
        const uitype = this.element.querySelector(`[data-key="uiType"]`);
        if (uitype)
            this.uiType = uitype.value;
        const uiInput = this.element.querySelector('[data-key="ui"]');
        if (uiInput)
            this.ui = uiInput.value;
        // Изменения «по месту» → onInputChanged обновит .bt-save-tc и промиррорит во внешнюю маску
        this.element.querySelector('.img-container .scene-nosound')
            ?.addEventListener('change', () => this.onInputChanged());
        this.element.querySelector('.scene-label')
            ?.addEventListener('change', () => this.onInputChanged());
        // ...
        // включаем обработчик "сохранить"
        this._wireSaveBtn();
        const savedVol = () => {
            try {
                const v = CookieService.get('player_volume');
                return v != null ? parseFloat(v) : null;
            }
            catch {
                return null;
            }
        };
        const volS = savedVol();
        if (volS != null && Number.isFinite(volS) && volS >= 0 && volS <= 1) {
            this.setVolume(volS);
            // если 0 — auto-mute (класс на кнопке ты уже ставишь в setVolume)
        }
        // Захват UI-элементов
        this.els.btnPlay = this._q('.bt-play');
        this.els.btnMute = this._q('.bt-mute');
        // Наши компоненты
        this.els.playProgress = this._q('.play-progress'); // <im-progress-bar>
        this.els.loadProgress = this._q('.load-progress'); // <im-progress-bar>
        this.els.playSlider = this._q('.play-slider'); // <im-slider>
        this.els.sceneRange = this._q('.scene-range'); // <im-slider range>
        this.els.volumeSlider = this._q('.volume-slider'); // <im-slider>
        this.els.title = this._q('.playing-title');
        this.els.tkContainer = this._q('.tk-container');
        this.els.imgContainer = this._q('.img-container');
        this.els.sceneContainer = this._q('.scene-container');
        this.els.labelContainer = this._q('.label-container');
        this.els.filmPartLinking = this._q('.film-part-linking');
        this.els.filmLinking = this._q('.film-linking');
        // Слушатели кнопок
        this.els.btnMute?.addEventListener('click', this._onMuteClick, { signal: this.abortController.signal });
        this.els.btnPlay?.addEventListener('click', this.playPause);
        this.mediaEl.addEventListener('click', this.playPause);
        // Инициализация наших слайдеров/прогрессбаров (без jQuery)
        this._wirePlaySlider();
        this._wireSceneRange();
        this._wireVolumeSlider(options);
        this.mediaEl.addEventListener('loadstart', this._onLoadingOn);
        this.mediaEl.addEventListener('waiting', this._onLoadingOn);
        this.mediaEl.addEventListener('stalled', this._onLoadingOn);
        this.mediaEl.addEventListener('seeking', this._onLoadingOn);
        this.mediaEl.addEventListener('canplay', this._onLoadingOff);
        this.mediaEl.addEventListener('playing', this._onLoadingOff);
        this.mediaEl.addEventListener('seeked', this._onLoadingOff);
        this.mediaEl.addEventListener('error', this._onLoadingOff);
        this.mediaEl.addEventListener('emptied', this._onLoadingOff);
        // Шаг из опций/разметки
        this._stepSeconds = options.stepSeconds ?? 1;
        // fps можно задать data-framerate (по умолчанию 25)
        this._frameRate = options.framerate ?? 25;
        // вертикальная ориентация громкости (VS)
        const volOrient = options.orientation ?? 'vertical';
        if (volOrient === 'vertical')
            this.els.volumeSlider?.setAttribute('vertical', '');
        else
            this.els.volumeSlider?.removeAttribute('vertical');
        // Кнопки транспорта (если есть — подключим)
        this.element.querySelector('.bt-prev-sec')?.addEventListener('click', this._onPrevSec);
        this.element.querySelector('.bt-next-sec')?.addEventListener('click', this._onNextSec);
        this.element.querySelector('.bt-prev-frame')?.addEventListener('click', this._onPrevFrame);
        this.element.querySelector('.bt-next-frame')?.addEventListener('click', this._onNextFrame);
        // Fullscreen в NF/VS работает через одну toggle-кнопку .bt-size-full.
        this.element.querySelector('.bt-size-full')?.addEventListener('click', this._toggleFullscreen);
        this._refreshControlsByState();
        // Инициализация состояния кнопок при старте
        this._onFullscreenChange();
        // 2026-03-26: в legacy nf-player слушал и префиксные fullscreen-события.
        // Без них в части браузеров/движков не обновлялось состояние size-0 и кнопок.
        document.addEventListener('fullscreenchange', this._onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this._onFullscreenChange);
        document.addEventListener('mozfullscreenchange', this._onFullscreenChange);
        document.addEventListener('MSFullscreenChange', this._onFullscreenChange);
        document.body.addEventListener('close-vsplayer', this._onCloseVs);
        if (options.poster && this.mediaEl instanceof HTMLVideoElement) {
            this.mediaEl.poster = options.poster;
        }
        if (options.playtime && this.mediaEl.src) {
            const t = parseFloat(options.playtime);
            this.mute(true);
            this.play();
            this.seek(t);
            if ((this.mediaEl.volume ?? 1) > 0)
                this.mute(false);
        }
        this.element.addEventListener('click', this._onCommonBtClick);
        this.element.tabIndex = 0;
        this.element.addEventListener('keyup', this._onKeyUp);
        this.element.addEventListener('keydown', this._onKeyDown);
        if (this.getOut() > 0) {
            var timeIn = this.getIn();
            this.setInOut(timeIn, this.getOut());
            // NOTE раньше в switchmode использовался единожды
            //this._skipFirstInit = true;
        }
        else {
            this.switchMode(PlayerMode.off);
        }
    }
    /**
     * @private Унифицированные хоткеи:
     *  Space — play/pause;
     *  Ctrl+Enter — быстрое выделение In/Out вокруг текущего t;
     *  Стрелки — шаг на _stepSeconds; Ctrl+стрелки — шаг по кадру (1/25).
     */
    _onKey(e, isUp) {
        // Пробел — play/pause на отпускание
        if (isUp && e.key === ' ') {
            this.playPause();
            return;
        }
        // Ctrl+Enter — быстрое выставление in/out вокруг текущей позиции
        if (isUp && e.key === 'Enter' && e.ctrlKey) {
            const t = this.engine.currentTime();
            const i = this.getIn();
            const o = this.getOut();
            if (t < i || (i === 0 && o === 0))
                this.setInOut(t, this.mediaEl.duration || o || t);
            else if (t > o)
                this.setInOut(i, t);
            else if (t < (i + o) / 2)
                this.setInOut(t, o);
            else
                this.setInOut(i, t);
            return;
        }
        // На зажатие — покадровые/секундные шаги
        if (!isUp) {
            let ct = this.engine.currentTime();
            if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && e.ctrlKey)
                ct -= 1 / 25;
            else if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && e.ctrlKey)
                ct += 1 / 25;
            else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
                ct -= this._stepSeconds;
            else if (e.key === 'ArrowRight' || e.key === 'ArrowDown')
                ct += this._stepSeconds;
            if (ct < 0)
                ct = 0;
            this.seek(ct);
        }
    }
    /** Значения In/Out/Img берём/кладём через dataset */
    getIn() { return parseFloat(this._getMask('.time-in')?.dataset.seconds ?? '0'); }
    getOut() { return parseFloat(this._getMask('.time-out')?.dataset.seconds ?? '0'); }
    getImg() { return parseFloat(this._getMask('.time-img')?.dataset.seconds ?? '0'); }
    /** Приватно: быстрый поиск элемента внутри виджета */
    _q(sel) { return this.element.querySelector(sel); }
    _getMask(sel) { return this.element.querySelector(sel); }
    _pauseOtherPlayers() {
        const others = Widget.getAll(PlayerBaseWidget);
        for (const it of others) {
            if (it !== this) {
                try {
                    it.pause();
                }
                catch { /* no-op */ }
            }
        }
    }
    /** public API: пауза/плей */
    play() {
        this._pauseOtherPlayers();
        // обычное воспроизведение — без сегментов и без лупа
        this._segmentActive = false;
        this.engine.setLoop({});
        this.engine?.play();
    }
    pause() { this.engine?.pause(); }
    /** Безопасный перевод на позицию в секундах */
    seek(sec) { this.engine?.seek(sec); }
    /** Поставить/снять mute и синхронизировать кнопку + ползунок громкости с восстановлением */
    mute(m) {
        if (m) {
            // запоминаем текущее ненулевое значение (берём из слайдера, иначе из media)
            const cur = parseFloat(this.els.volumeSlider?.dataset.value ?? String(this.mediaEl.volume ?? 1));
            if (Number.isFinite(cur) && cur > 0) {
                this._prevVolume = Math.min(1, Math.max(0, cur));
            }
            // уходим в 0 (setVolume сам обновит mute-кнопку и cookie)
            this.setVolume(0);
            if (this.els.volumeSlider)
                this.els.volumeSlider.dataset.value = '0';
            this.els.btnMute?.classList.add('mute');
        }
        else {
            // восстанавливаем прежнюю громкость, если она была; иначе из cookie; иначе 1
            let v = (this._prevVolume != null && this._prevVolume > 0) ? this._prevVolume : NaN;
            if (!Number.isFinite(v) || v <= 0) {
                try {
                    const c = parseFloat(CookieService.get('player_volume') ?? '');
                    if (Number.isFinite(c) && c > 0)
                        v = c;
                }
                catch { /* no-op */ }
            }
            if (!Number.isFinite(v) || v <= 0)
                v = 1;
            this.setVolume(v);
            if (this.els.volumeSlider)
                this.els.volumeSlider.dataset.value = String(v);
            this.els.btnMute?.classList.remove('mute');
        }
    }
    setVolume(v) {
        v = Math.max(0, Math.min(1, v));
        this.engine.setVolume(v);
        // 🔧 Берём живой элемент, если по какой-то причине ссылка в els пустая
        const volEl = this.els.volumeSlider
            ?? this.element.querySelector('.volume-slider');
        // Всегда синхронизируем data-value (мы не трогаем «ручной» drag по событиям,
        // так что блокировка по _draggingVolume тут не нужна — это программное изменение)
        if (volEl)
            volEl.dataset.value = String(v);
        // автопереключение mute-кнопки и самого плеера
        if (v === 0) {
            if (!this.mediaEl.muted)
                this.engine.setMuted(true);
            this.els.btnMute?.classList.add('mute');
        }
        else {
            if (this.mediaEl.muted)
                this.engine.setMuted(false);
            this.els.btnMute?.classList.remove('mute');
        }
        try {
            CookieService.set('player_volume', String(v));
        }
        catch { /* no-op */ }
    }
    setIn(valueIn) {
        var valueOut = this.getOut();
        if (valueOut <= valueIn) {
            this.setInOut(valueIn, this.engine.duration());
        }
        else
            this.setInOut(valueIn, valueOut);
    }
    setOut(valueOut) {
        var valueIn = this.getIn();
        if (valueIn >= valueOut)
            this.setInOut(0, valueOut);
        else
            this.setInOut(valueIn, valueOut);
    }
    /** Установить границы In/Out; hide если оба не заданы */
    setInOut(valueIn, valueOut, raw) {
        const tk = this.els.tkContainer;
        const scn = this.els.sceneContainer;
        if (valueIn != null && valueOut != null) {
            this._setTimeValue('.time-in', valueIn);
            this._setTimeValue('.time-out', valueOut);
            if (tk)
                DomHelpers.show(tk);
            if (scn)
                DomHelpers.show(scn);
            this._sceneRangeSet([valueIn, valueOut]); // внутри — снимем disabled
            this.onInputChanged();
            // по желанию: снять .off с кнопок внутри .tk-container (паритет с легаси)
            this.element.querySelectorAll('.tk-container .bt').forEach(b => b.classList.remove('disabled'));
            switch (this.currentMode) {
                case PlayerMode.preview: {
                    this.switchMode(PlayerMode.newFootage);
                    break;
                }
                case PlayerMode.playWebFootage: {
                    this.switchMode(PlayerMode.newScene);
                    break;
                }
            }
        }
        else {
            this._setTimeValue('.time-in', 0, '--:--:--:--');
            this._setTimeValue('.time-out', 0, '--:--:--:--');
            if (tk)
                DomHelpers.hide(tk);
            if (scn)
                DomHelpers.hide(scn);
            // по желанию: задать .off на кнопки
            this.element.querySelectorAll('.tk-container .bt').forEach(b => b.classList.add('disabled'));
        }
    }
    /** Установить титульный кадр */
    setImg(valueImg) {
        const imgc = this.els.imgContainer;
        if (valueImg != undefined) {
            // Квантование по 1/25 как в легаси
            const v = Math.floor(valueImg) + Math.floor(25 * (valueImg - Math.floor(valueImg))) / 25;
            this._setTimeValue('.time-img', v, undefined, true);
            imgc && DomHelpers.show(imgc);
        }
        else {
            this._setTimeValue('.time-img', 0, '--:--:--:--', true);
            imgc && DomHelpers.hide(imgc);
        }
        this.onInputChanged();
    }
    /** Отрисовка заголовка */
    _setTitle(html) { if (this.els.title)
        this.els.title.innerHTML = html ?? ''; }
    /** Вызов серверной команды  */
    _execCmd(cmd, options, noLoading = false) {
        if (!noLoading)
            this._setLoading(true);
        const controllerView = this.ensureControllerView();
        ViewWidgetHelper.ExecCmd(this.element, {
            cmd: cmd,
            options: options ?? {},
            cmdType: ViewCmdType.onchange,
            onSuccess: (data) => {
                this._setLoading(false);
                this._modeNormal();
                if (!data)
                    return;
                // Хук для переопределения в наследнике
                if (this.onBeforePlayerCommand?.(data) === true) ;
                switch (data.type) {
                    case PlayerServerCommand.play:
                        // Пришли данные по dto
                        this.doPlayFile(data.fileID, data.title, data.url, data.mode, data);
                        break;
                }
            },
            onError: () => {
                this._modeNormal();
                console.error('request error');
                this._setLoading(false);
            }
        }, controllerView);
    }
    ensureControllerView() {
        let view = document.querySelector('.im-player-controller');
        if (!view) {
            const div = document.createElement('div');
            div.classList.add('im-view', 'im-player-controller');
            div.dataset.cfg = this.controller;
            // Скрываем по умолчанию
            div.style.display = 'none';
            // Добавляем в конец тела документа
            document.body.appendChild(div);
            // Инициализируем контроллер как ViewWidget
            ViewWidget.getOrInit(div);
            view = div;
        }
        return view;
    }
    /** Включить «busy» для .cmd-disabled */
    _modeBusy() { this.element.querySelectorAll('.cmd-disabled').forEach(b => b.classList.add('off')); }
    /** Снять «busy» */
    _modeNormal() { this.element.querySelectorAll('.cmd-disabled').forEach(b => b.classList.remove('off')); }
    /** Переключение режима (скелет) */
    switchMode(mode, title, options) {
    }
    /** Вызывается при любом пользовательском изменении */
    onInputChanged() {
        // Дочерние классы могут подсветить кнопку сохранения
        this.element.querySelector('.bt-save-tc')?.classList.remove('disabled');
    }
    /** Установка значения времени в маску */
    _setTimeValue(maskSel, seconds, timecode, putText) {
        const elem = this._getMask(maskSel);
        if (!elem)
            return;
        if (elem instanceof HTMLInputElement) {
            const tw = TimecodeWidget.getOrInit(elem);
            tw.setValue(seconds);
        }
        else {
            if (putText) {
                const tc = TimecodeWidget.fromSecondsToHMSF(seconds, this._frameRate);
                elem.textContent = tc;
            }
        }
        elem.dataset.seconds = String(seconds);
    }
    _onFullscreenStateChanged(_isFullscreen) { }
    // --- Engine callbacks ---
    _onEngineTime(t) {
        // обновляем play-progress (в процентах)
        const dur = this.mediaEl.duration || 0;
        if (this.els.playProgress && dur > 0) {
            const pct = Math.max(0, Math.min(100, Math.round((t / dur) * 100)));
            this.els.playProgress.setAttribute('value', String(pct));
        }
        // однократное воспроизведение фрагмента: остановиться на out
        if (this._segmentActive && this.mediaEl.duration) {
            if (t >= Math.min(this._segmentEnd, this.mediaEl.duration)) {
                this.pause();
                this._segmentActive = false;
                // важно: не делаем seek назад — по требованиям должен остановиться
            }
        }
        // синхронизируем play-slider, если пользователь не тащит его сейчас
        if (!this._draggingPlay && this.els.playSlider) {
            this.els.playSlider.dataset.value = String(t);
        }
        // Обновляем метку «текущее время» (VS-совместимость)
        this._updateCurrentTimeLabel(t);
    }
    /** Обновить метку текущего времени (формат mm:ss) */
    _updateCurrentTimeLabel(t) {
        const secs = Math.floor(Math.max(0, t || 0));
        const mm = String(Math.floor(secs / 60)).padStart(2, '0');
        const ss = String(secs % 60).padStart(2, '0');
        const el = this.element.querySelector('.im-current-time');
        if (el)
            el.textContent = `${mm}:${ss}`;
    }
    /** Проиграть заданный отрезок [from..to] без зацикливания */
    playSegment(from, to) {
        this.engine.setLoop({});
        this._segmentActive = true;
        this._segmentEnd = to;
        this.seek(from);
        this.engine.play();
    }
    _onEngineDuration(d) {
        const stepFrame = 1 / this._frameRate;
        if (this.els.playSlider) {
            this.els.playSlider.setAttribute('min', '0');
            this.els.playSlider.setAttribute('max', String(Math.max(0, d || 0)));
            this.els.playSlider.setAttribute('step', String(stepFrame));
        }
        if (this.els.sceneRange) {
            this.els.sceneRange.setAttribute('min', '0');
            this.els.sceneRange.setAttribute('max', String(Math.max(0, d || 0)));
            this.els.sceneRange.setAttribute('range', '');
            this.els.sceneRange.setAttribute('step', String(stepFrame));
        }
    }
    _onEngineProgress(bufferedEndSec) {
        if (!this.els.loadProgress)
            return;
        const dur = this.mediaEl.duration || 0;
        if (!dur)
            return;
        const pct = Math.max(0, Math.min(100, Math.round((bufferedEndSec / dur) * 100)));
        this.els.loadProgress.setAttribute('value', String(pct));
    }
    // --- Вспомогательные методы для scene-range ---
    _sceneRangeSet(values) {
        const s = this.els.sceneRange;
        if (!s)
            return;
        // гарантируем включённый слайдер
        s.removeAttribute('disabled');
        s.dataset.values = `${values[0]},${values[1]}`;
    }
    /** Play slider (позиция) — <im-slider class="play-slider"> */
    _wirePlaySlider() {
        const s = this.els.playSlider;
        if (!s)
            return;
        s.setAttribute('min', '0');
        s.setAttribute('max', String(this.mediaEl.duration || 0));
        s.setAttribute('step', String(1 / this._frameRate));
        s.dataset.value = String(this.mediaEl.currentTime || 0);
        const onStart = () => { this._draggingPlay = true; };
        const onEnd = () => { this._draggingPlay = false; };
        this._onPlayInput = (ev) => {
            const host = ev.currentTarget;
            const cur = parseFloat(host.dataset.value ?? '0');
            if (Number.isFinite(cur))
                this.seek(cur);
        };
        this._onPlayChange = (ev) => {
            const host = ev.currentTarget;
            const cur = parseFloat(host.dataset.value ?? '0');
            if (Number.isFinite(cur))
                this.seek(cur);
            onEnd();
            // событие, как в легаси — по паузе отдаём текущее время наружу
            if (this.mediaEl.paused) {
                this.element.dispatchEvent(new CustomEvent('player-time-changed', {
                    detail: { timecode: this.engine.currentTime() }
                }));
            }
        };
        s.addEventListener('mousedown', onStart);
        s.addEventListener('touchstart', onStart);
        s.addEventListener('keyup', onStart);
        s.addEventListener('input', this._onPlayInput);
        s.addEventListener('change', this._onPlayChange);
        this._offPlaySlider = () => {
            s.removeEventListener('mousedown', onStart);
            s.removeEventListener('touchstart', onStart);
            s.removeEventListener('keyup', onStart);
            if (this._onPlayInput)
                s.removeEventListener('input', this._onPlayInput);
            if (this._onPlayChange)
                s.removeEventListener('change', this._onPlayChange);
        };
    }
    // ==========================================
    /** Проиграть исходник по videosourceID */
    playVideosource(videosourceID) {
        const caps = this.engine.canPlayTypes(); // { h264?: boolean, webm?: boolean }
        this._execCmd(PlayerViewCmd.PlayVideosourceById, { videosourceID, ...caps });
    }
    /** Проиграть фильм (film) */
    playFilm(filmID, orderID, rowID) {
        const caps = this.engine.canPlayTypes();
        this._execCmd(PlayerViewCmd.PlayFilmById, { filmID, orderID, rowID, ...caps });
    }
    /** Проиграть файл (file) */
    playFile(fileID) {
        this._execCmd(PlayerViewCmd.PlayFileById, { fileID: fileID });
    }
    playFootageById(options) {
        this._execCmd(PlayerViewCmd.PlayFootageById, options);
    }
    saveChangedFootage(options) {
        this._execCmd(PlayerViewCmd.SaveChangedFootageById, options);
    }
    saveNewFootage(options) {
        this._execCmd(PlayerViewCmd.SaveNewFootageByFile, options);
    }
    saveSceneByFootage(options) {
        this._execCmd(PlayerViewCmd.SaveSceneByFootage, options);
    }
    /** Проиграть кройку (footage) c опциональными timeIn/Out и связками заказа/строки */
    playFootage(footageID, timeIn, timeOut, orderID, rowID) {
        if (this.currentModeOptions != undefined && footageID == this.currentModeOptions.footageID
            && timeIn != undefined && timeOut == undefined
            && orderID == undefined && rowID == undefined) {
            this.seek(timeIn);
            return;
        }
        if (timeIn && timeIn > 0) {
            this.element.classList.add('nf-player-hidden');
            this.showPlayertime = timeIn;
        }
        const caps = this.engine.canPlayTypes();
        this.playFootageById({
            footageID: footageID,
            timeIn: timeIn,
            timeOut: timeOut,
            orderID: orderID,
            rowID: rowID,
            ...caps
        });
    }
    doPlayFile(fileID, title, url, mode, options) {
        if (!options)
            options = {};
        const pOptions = options;
        if (!pOptions.fileID)
            pOptions.fileID = fileID;
        if (url && url != this.mediaEl.src) {
            this.mediaEl.src = url;
            if (pOptions.poster != undefined && pOptions.poster != '')
                this._setPoster(pOptions.poster);
            else
                this._setPoster();
        }
        const playerMode = mode;
        if (playerMode)
            this.switchMode(playerMode, title, pOptions);
        if (pOptions.timeOut && pOptions.timeOut > 0 && pOptions.timeIn && pOptions.timeIn >= 0) {
            this.setInOut(pOptions.timeIn, pOptions.timeOut, true);
            this.setImg(pOptions.titleImgTime == null ? pOptions.timeIn + (pOptions.timeOut - pOptions.timeIn) / 4 : pOptions.titleImgTime);
            this.seek(pOptions.timeIn);
        }
        DomHelpers.show(this.element);
        //this.initChangeSizePlayer();
        this.play();
    }
    _setPoster(poster) {
        if (poster)
            this.mediaEl.style.backgroundImage = `url("${poster}")`;
        else
            this.mediaEl.style.backgroundImage = '';
    }
    /** Scene range — <im-slider class="scene-range" range> */
    /** Scene range — <im-slider class="scene-range" range> */
    _wireSceneRange() {
        const s = this.els.sceneRange;
        if (!s)
            return;
        s.setAttribute('range', '');
        s.setAttribute('min', '0');
        s.setAttribute('max', String(this.mediaEl.duration || 0));
        s.setAttribute('step', String(1 / this._frameRate));
        const onStart = () => { this._draggingRange = true; };
        const onEnd = () => { this._draggingRange = false; };
        s.addEventListener('mousedown', onStart);
        s.addEventListener('touchstart', onStart);
        /**
         * @private Живой предпросмотр во время перетаскивания:
         * определяем, какая ручка сместилась, и делаем seek к её значению.
         * Без установки лупа: одноразовый предпросмотр.
         */
        this._onRangeInput = (ev) => {
            const host = ev.currentTarget;
            const vals = (host.dataset.values || '')
                .split(',')
                .map(v => +v.trim());
            if (vals.length < 2 || !vals.every(Number.isFinite))
                return;
            // Определяем сместившуюся ручку относительно прошлых значений
            let movedIndex = 0; // по умолчанию — левая
            if (this._lastRangePair) {
                const [pL, pR] = this._lastRangePair;
                const [nL, nR] = vals;
                const dL = Math.abs((nL ?? 0) - (pL ?? 0));
                const dR = Math.abs((nR ?? 0) - (pR ?? 0));
                movedIndex = dR > dL ? 1 : 0;
            }
            const preview = vals[movedIndex];
            // живой просмотр
            if (Number.isFinite(preview)) {
                // не включаем луп; просто «подсматриваем»
                this.seek(preview);
                // синхронизируем play-слайдер/прогресс, пока тянем
                if (this.els.playSlider)
                    this.els.playSlider.dataset.value = String(preview);
                const dur = this.mediaEl.duration || 0;
                if (this.els.playProgress && dur > 0) {
                    const pct = Math.max(0, Math.min(100, Math.round((preview / dur) * 100)));
                    this.els.playProgress.setAttribute('value', String(pct));
                }
            }
        };
        /**
         * @private По отпусканию фиксируем In/Out + авто-возврат Img на 20%,
         * если выпал из диапазона (как в легаси).
         */
        this._onRangeChange = (ev) => {
            const host = ev.currentTarget;
            const vals = (host.dataset.values || '')
                .split(',')
                .map(v => +v.trim());
            if (vals.length >= 2 && vals.every(Number.isFinite)) {
                const [l, r] = vals;
                this._setTimeValue('.time-in', l);
                this._setTimeValue('.time-out', r);
                this.onInputChanged();
                const i = this.getIn(), o = this.getOut(), img = this.getImg();
                if (Number.isFinite(i) && Number.isFinite(o) && !(img >= i && img <= o)) {
                    this.setImg(i + (o - i) * 0.2);
                }
                // обновляем «последнюю» пару
                this._lastRangePair = [l, r];
            }
            onEnd();
        };
        s.addEventListener('input', this._onRangeInput);
        s.addEventListener('change', this._onRangeChange);
        this._offSceneRange = () => {
            s.removeEventListener('mousedown', onStart);
            s.removeEventListener('touchstart', onStart);
            if (this._onRangeInput)
                s.removeEventListener('input', this._onRangeInput);
            if (this._onRangeChange)
                s.removeEventListener('change', this._onRangeChange);
        };
    }
    /** Volume slider — <im-slider class="volume-slider"> */
    _wireVolumeSlider(options) {
        const s = this.els.volumeSlider;
        if (!s)
            return;
        s.setAttribute('min', '0');
        s.setAttribute('max', '1');
        s.setAttribute('step', '0.01');
        s.dataset.value = String(this.mediaEl.volume ?? 1);
        const onStart = () => { this._draggingVolume = true; };
        const onEnd = () => { this._draggingVolume = false; };
        this._onVolInput = (ev) => {
            const host = ev.currentTarget;
            const v = parseFloat(host.dataset.value ?? '0');
            if (Number.isFinite(v))
                this.setVolume(Math.max(0, Math.min(1, v)));
        };
        this._onVolChange = (ev) => {
            const host = ev.currentTarget;
            const v = parseFloat(host.dataset.value ?? '0');
            if (Number.isFinite(v))
                CookieService.set('player_volume', String(v));
            onEnd();
        };
        s.addEventListener('mousedown', onStart);
        s.addEventListener('touchstart', onStart);
        s.addEventListener('input', this._onVolInput);
        s.addEventListener('change', this._onVolChange);
        this._offVolume = () => {
            s.removeEventListener('mousedown', onStart);
            s.removeEventListener('touchstart', onStart);
            if (this._onVolInput)
                s.removeEventListener('input', this._onVolInput);
            if (this._onVolChange)
                s.removeEventListener('change', this._onVolChange);
        };
    }
    /** Быстрая валидация диапазона и titleImg */
    _validateInOutImgAndExecute(validateToClose) {
        const dur = this.mediaEl.duration || 0;
        let i = this.getIn(), o = this.getOut(), img = this.getImg();
        if (!(isFinite(i) && isFinite(o)) || o <= i) {
            ImSys.alert(Loc.error, Loc.startEndTimecodeNotSpecified); // Не задан диапазон In/Out или задан некорректно
            return;
        }
        if (validateToClose && (i <= 0 || o >= Math.max(1, dur - 1))) {
            // Начало/конец слишком близко к границам файла, выводим предупреждение с возможностью подтвердить
            ImSys.alertConfirm(Loc.warning, Loc.startEndTooCloseToFileBoundaries, () => {
                // Повторная проверка с отключенной проверкой validateToClose
                this._validateInOutImgAndExecute(false);
            });
            return;
        }
        else {
            if (i < 0)
                i = 0;
            if (o > dur)
                o = dur;
        }
        if (!(img >= i && img <= o))
            ImSys.alert(Loc.error, Loc.timecodeTitleImageIncorrect); // Время титульного кадра вне диапазона In/Out.
        // Непосредственное выполнение команды
        this._onSaveTcRequested();
    }
    /** Вызывается при клике на .bt-save-tc — делегируем наследнику */
    _onSaveTcRequested() {
    }
    /** навешиваем делегат на .bt-save-tc (в _create уже есть общий делегат .bt ...) */
    _wireSaveBtn() {
        this.element.addEventListener('click', (e) => {
            const btn = e.target.closest('.bt-save-tc');
            if (!btn || !this.element.contains(btn))
                return;
            this._validateInOutImgAndExecute(this.validateInOut);
        });
    }
    // --- destroy ---
    _onDestroy() {
        try {
            this.engine?.dispose();
        }
        catch { }
        this.els.btnMute?.removeEventListener('click', this._onMuteClick);
        this.els.btnPlay?.removeEventListener('click', this.playPause);
        this.mediaEl?.removeEventListener('click', this.playPause);
        try {
            this._offPlaySlider?.();
        }
        catch { }
        try {
            this._offSceneRange?.();
        }
        catch { }
        try {
            this._offVolume?.();
        }
        catch { }
        this.element.querySelector('.bt-prev-sec')?.removeEventListener('click', this._onPrevSec);
        this.element.querySelector('.bt-next-sec')?.removeEventListener('click', this._onNextSec);
        this.element.querySelector('.bt-prev-frame')?.removeEventListener('click', this._onPrevFrame);
        this.element.querySelector('.bt-next-frame')?.removeEventListener('click', this._onNextFrame);
        this.mediaEl?.removeEventListener('loadstart', this._onLoadingOn);
        this.mediaEl?.removeEventListener('waiting', this._onLoadingOn);
        this.mediaEl?.removeEventListener('stalled', this._onLoadingOn);
        this.mediaEl?.removeEventListener('seeking', this._onLoadingOn);
        this.mediaEl?.removeEventListener('canplay', this._onLoadingOff);
        this.mediaEl?.removeEventListener('playing', this._onLoadingOff);
        this.mediaEl?.removeEventListener('seeked', this._onLoadingOff);
        this.mediaEl?.removeEventListener('error', this._onLoadingOff);
        this.mediaEl?.removeEventListener('emptied', this._onLoadingOff);
        this.element.removeEventListener('keyup', this._onKeyUp);
        this.element.removeEventListener('keydown', this._onKeyDown);
        this.element.removeEventListener('click', this._onCommonBtClick);
        this.element.querySelector('.bt-size-full')?.removeEventListener('click', this._toggleFullscreen);
        document.removeEventListener('fullscreenchange', this._onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this._onFullscreenChange);
        document.removeEventListener('mozfullscreenchange', this._onFullscreenChange);
        document.removeEventListener('MSFullscreenChange', this._onFullscreenChange);
        if (this._onCloseVs)
            document.body.removeEventListener('close-vsplayer', this._onCloseVs);
    }
}

export { PlayerBaseWidget };
